/**
 * SFrame250 – Close the Estate: Phase Overview
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PhaseOverview, type PhaseStepState } from '../settlement';
import { useSettlementSession } from '../../hooks/useSettlementSession';
import { SETTLEMENT_PHASE_CONFIGS } from '../../utils/settlementFlowConfig';
import { SETTLEMENT_PLANNER_LAYOUT } from '../settlement/settlementTheme';

const config = SETTLEMENT_PHASE_CONFIGS['close'];

const stepRoutes: Record<string, string> = {
  'cls-1': '/sframe-251',
  'cls-2': '/sframe-253',
  'cls-3': '/sframe-255',
  'cls-4': '/sframe-257',
};

export default function SFrame250() {
  const { estateId } = useParams<{ estateId: string }>();
  const navigate = useNavigate();
  const { loadPhaseProgress, error } = useSettlementSession(estateId ?? null);

  const [steps, setSteps] = useState<PhaseStepState[]>(
    config.steps.map((step) => ({
      step, status: 'not-started',
      subTaskProgress: { completed: 0, total: step.subTasks?.reduce((sum, st) => sum + (st.checklistItems?.length || 0), 0) || 0 },
    }))
  );

  useEffect(() => {
    if (!estateId) return;
    const load = async () => {
      const progressData = await loadPhaseProgress('close');
      setSteps(config.steps.map((step) => {
        const progress = progressData.find((p) => p.stepId === step.id);
        const totalItems = step.subTasks?.reduce((sum, st) => sum + (st.checklistItems?.length || 0), 0) || 0;
        return { step, status: (progress?.status as PhaseStepState['status']) || 'not-started', subTaskProgress: { completed: progress?.completedChecklistItems?.length || 0, total: totalItems } };
      }));
    };
    load();
  }, [estateId, loadPhaseProgress]);

  const handleStepClick = (stepId: string) => {
    const route = stepRoutes[stepId];
    if (route) navigate(estateId ? `${route}/${estateId}` : route);
  };

  return (
    <>
      {error && (
        <div className={`${SETTLEMENT_PLANNER_LAYOUT.container} mb-4`}>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>
        </div>
      )}
      <PhaseOverview
        title="Close the Estate"
        subtitle="Finalize accounting, complete approvals, close accounts, and archive records to officially settle the estate."
        layoutVariant="planner"
        sectionOrder="next-first"
        steps={steps}
        estimatedMinutes={steps.filter((s) => s.status !== 'complete').reduce((sum, s) => sum + (s.step.estimatedMinutes || 0), 0)}
        onStepClick={handleStepClick}
      />
    </>
  );
}
